import {
  type LoadState,
  stateInitial,
  stateLoading,
  stateError,
  stateLoaded,
} from "@shared/models/state";
import { type MyAccountData, getUserSelf } from "@shared/services/myAccount";
import {
  patchUser,
  type KeyCloakUserPatch,
} from "@shared/services/userManagementKeyCloak";
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  applicationLanguage,
  isConfiguredLanguage,
  languageLocale,
  setLanguage as setApplicationLanguage,
} from "@shared/util/languages";
import { useAuthStore } from "./auth";

export const useMyAccountStore = defineStore("myAccount", () => {
  const language = applicationLanguage;
  const authStore = useAuthStore();

  function setLanguage(langCode: string) {
    setApplicationLanguage(langCode);
  }

  const locale = computed(() => languageLocale.value);

  const userDataLoadingState = ref<LoadState<MyAccountData>>({
    ...stateInitial,
  });

  const userData = computed<MyAccountData | undefined>(
    () => userDataLoadingState.value.result,
  );

  async function initUserData(reload = true) {
    if (!reload && userDataLoadingState.value.loaded) {
      return;
    }

    userDataLoadingState.value = { ...stateLoading };
    const response = await getUserSelf().catch((e) => {
      userDataLoadingState.value = { ...stateError };
      throw e;
    });
    userDataLoadingState.value = { ...stateLoaded, result: response.data };

    const userLanguage = response.data.attributes.language[0];
    if (userLanguage && isConfiguredLanguage(userLanguage)) {
      setApplicationLanguage(userLanguage);
    } else if (userLanguage) {
      console.warn(`Unsupported user language: ${userLanguage}`);
    }
  }

  async function updateUserData(data: KeyCloakUserPatch) {
    if (userData.value?.id === undefined) {
      throw Error(
        "MyAccountStore: Trying to update user, but data is not loaded",
      );
    }
    await patchUser(userData.value.id, data);
    await authStore.refreshToken();
    await initUserData(true);
  }

  return {
    setLanguage,
    language,
    locale,
    initUserData,
    userDataLoadingState,
    userData,
    updateUserData,
  };
});
